<template>
    <Multiselect
        :ref="'multiselect'"
        v-model="localValue"
        :options="options"
        :multiple="multiple"
        :close-on-select="!multiple"
        :clear-on-select="clearOnSelect"
        :preserve-search="false"
        :placeholder="placeholder"
        :label="itemLabel"
        :value="itemValue"
        :track-by="itemLabel"
        :show-labels="false"
        :group-label="groupLabel"
        :group-select="groupSelect"
        :group-values="groupValues"
        :disabled="disabled"
        :hide-selected="hideSelected"
        :searchable="searchable"
        :allow-empty="allowEmpty"
        @search-change="$emit('search-change', $event)"
        @select="$emit('select', $event)"
        @close="$emit('close', $event)"
        @open="$emit('open', $event)"
        @remove="$emit('remove', $event)"
        :loading="loading"
        :class="{ 'is-loading': loading }"
        :open-direction="openDirection"
    >
        <template #noResult>
            {{ searchNoOptionsText }}
        </template>
        <template #noOptions>
            {{ searchNoOptionsText }}
        </template>
        <template #option="props">
            <span v-if="itemLabel && tooltipLabel" v-b-tooltip.hover.bottomright.html="{ title: formatTooltipText(props.option[tooltipLabel]) }" class="full-width">
                {{ props.option['$groupLabel'] ?? props.option[itemLabel] }}
            </span>
            <span v-else-if="itemLabel && !tooltipLabel" class="full-width">
                {{ props.option['$groupLabel'] ?? props.option[itemLabel] }}
            </span>
            <span v-else>
                {{ props.option }}
            </span>
        </template>

        <template v-slot:tag="{ option, remove }">
            <div class="multiselect__tag" :class="{'hidden': hideInactive && option.hasOwnProperty('active') && option?.active !== 1}">
                <span :class="{'item-bold': option?.isTextBold}">{{ option[tagLabel || itemLabel] ?? option[itemLabel] ?? option }}</span>
                <i class="multiselect__tag-icon" @click="remove(option, $event)" />
            </div>
        </template>

        <template #caret>
            <div class="caret-wrapper">
                <slot name="caret-add-btn" />
                <div @mousedown.prevent.stop="$refs.multiselect.toggle()" class="multiselect__select"></div>
                <span
                    v-if="canShowDeselectButton"
                    :class=" multiple ? 'multi-deselect-icon' : 'single-deselect-icon'"
                    @click="deselectValue"
                />
            </div>
        </template>
    </Multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import { confirm } from '@/js/services/confirm-service';
// import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
    name: "multiselect",
    components: {
        Multiselect,
    },
    props: {
        value: {
            type: [String, Number, Object, Array],
        },
        options: {
            default: () => [],
            type: Array,
        },
        multiple: {
            default: true,
            type: Boolean,
        },
        placeholder: {
            type: String,
        },
        itemLabel: {
            type: String,
        },
        tagLabel: {
            type: String,
        },
        itemValue: {
            type: String,
        },
        groupLabel: {
            type: String,
        },
        groupSelect: {
            default: false,
            type: Boolean,
        },
        groupValues: {
            type: String,
        },
        disabled: {
            default: false,
            type: Boolean,
        },
        hideSelected: {
            default: false,
            type: Boolean,
        },
        searchable: {
            default: true,
            type: Boolean,
        },
        clearOnSelect: {
            default: true,
            type: Boolean,
        },
        allowEmpty: {
            default: true,
            type: Boolean,
        },
        emptyValue: {
            default: null
        },
        tooltipLabel: {
            type: String,
        },
        searchNoResultText: {
            type: String,
            default: 'Sorry, no matching options.'
        },
        searchNoOptionsText: {
            type: String,
            default: 'Sorry, no matching options.'
        },
        loading: {
            default: false,
            type: Boolean,
        },
        openDirection: {
            type: String,
            default: 'bottom'
        },
        hideInactive: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {

        }
    },
    computed: {
        localValue: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        canShowDeselectButton(){
            return this.allowEmpty && !this.valueIsEmpty;
        },
        valueIsEmpty() {
            return  Array.isArray(this.value) ? this.value.length == 0 : !this.value;
        }
    },
    watch: {

    },
    methods: {
        focus() {
            this.$refs.multiselect.activate();
        },
        deactivate() {
            this.$refs.multiselect.deactivate();
        },
        formatTooltipText(text) {
            return text ? ("<span class='tooltip-word-wrap'>" + text + "</span>") : '';
        },
        async deselectValue(){
            let emptyValue = this.multiple ? [] : this.emptyValue;
            const ok = await confirm('Are you sure you want to clear all options from the field?');
            if (!this.multiple || ok)
                this.$emit('input', emptyValue);
        },
    }
};
</script>

<style>

.tooltip-word-wrap {
    word-break: break-word;
    white-space: normal;
}

fieldset[disabled] .multiselect {
    pointer-events: none;
}

.is-loading .multiselect__select {
    display: none;
}


.multiselect__spinner {
    position: absolute;
    right: 1px;
    top: 1px;
    width: 48px;
    height: 15px;
    background: #fff;
    display: block;
}

.multiselect__spinner:after,
.multiselect__spinner:before {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    /*margin: -8px 0 0 -8px;*/
    width: 16px;
    height: 16px;
    border-radius: 100%;
    border: 2px solid transparent;
    border-top-color: #41b883;
    box-shadow: 0 0 0 1px transparent;
}

.multiselect__spinner:before {
    animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
    animation-iteration-count: infinite;
}

.multiselect__spinner:after {
    animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
    animation-iteration-count: infinite;
}

.multiselect__loading-enter-active,
.multiselect__loading-leave-active {
    transition: opacity 0.4s ease-in-out;
    opacity: 1;
}

.multiselect__loading-enter,
.multiselect__loading-leave-active {
    opacity: 0;
}

.multiselect,
.multiselect__input,
.multiselect__single {
    font-family: inherit;
    font-size: 14px;
    -ms-touch-action: manipulation;
    touch-action: manipulation;
}

.multiselect {
    box-sizing: content-box;
    display: block;
    position: relative;
    width: 100%;
    min-height: 35px;
    text-align: left;
    color: #35495e;
}

.multiselect * {
    box-sizing: border-box;
}

.multiselect:focus {
    outline: none;
}

.multiselect--disabled {
    /*background: #ededed;*/
    cursor: not-allowed;
    opacity: 0.6;
}

.multiselect--active {
    z-index: 50;
}

.multiselect--active:not(.multiselect--above) .multiselect__current,
.multiselect--active:not(.multiselect--above) .multiselect__input,
.multiselect--active:not(.multiselect--above) .multiselect__tags {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.multiselect--active .multiselect__input {
    padding-left: 5px !important;
}

.multiselect--active .multiselect__select {
    transform: rotate(180deg);
}

.multiselect--above.multiselect--active .multiselect__current,
.multiselect--above.multiselect--active .multiselect__input,
.multiselect--above.multiselect--active .multiselect__tags {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.multiselect__input,
.multiselect__single {
    position: relative;
    display: inline-block;
    min-height: 20px;
    line-height: 20px;
    border: none;
    border-radius: 5px;
    background: #fff;
    padding: 0 0 0 5px;
    width: 100%;
    transition: border 0.1s ease;
    box-sizing: border-box;
    margin-top: 3px;
    margin-bottom: 3px;
    vertical-align: top;
}

.multiselect__input:-ms-input-placeholder {
    color: #bfbfbf;
}

.multiselect__input::placeholder {
    color: #bfbfbf;
}

.multiselect__tag ~ .multiselect__input,
.multiselect__tag ~ .multiselect__single {
    width: auto;
}

.multiselect__input:hover,
.multiselect__single:hover {
    border-color: #cfcfcf;
}

.multiselect__input:focus,
.multiselect__single:focus {
    border-color: #a8a8a8;
    outline: none;
}

.multiselect__single {
    padding-left: 5px;
    margin: 3px 0px 3px 0px;
    overflow: hidden;
}

.multiselect__tags-wrap {
    display: block;
    max-height: 554px;
    overflow: auto;
}

.multiselect__tags {
    min-height: 34px;
    display: block;
    padding: 4px 28px 1px 4px;
    border-radius: 5px;
    border: 1px solid #cdcdcd;
    background: #fff;
    font-size: 14px;
}

.multiselect__tag {
    position: relative;
    display: inline-block;
    padding: 4px 26px 4px 10px;
    border-radius: 5px;
    margin-right: 5px;
    color: #333;
    line-height: 1;
    background: #f0f0f0;
    white-space: nowrap;
    overflow: hidden;
    max-width: 97%;
    text-overflow: ellipsis;
}
.single-deselect-icon,
.multi-deselect-icon {
    cursor: pointer;
    margin-left: 5px;
    position: absolute;
    /*right: -10px;*/
    /*top: -2px;*/
    right: 20px;
    top: 6px;
    bottom: 0;
    font-weight: 700;
    font-style: normal;
    width: 22px;
    text-align: center;
    line-height: 22px;
    transition: all 0.2s ease;
    border-radius: 5px;
}
.single-deselect-icon:after {
    content: "\D7";
    color: #999999;
    font-size: 14px;
}
.multi-deselect-icon:after {
    content: "\D7";
    color: red;
    font-size: 14px;
}
.multiselect__tag-icon {
    cursor: pointer;
    margin-left: 7px;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    font-weight: 700;
    font-style: normal;
    width: 22px;
    text-align: center;
    line-height: 22px;
    transition: all 0.2s ease;
    border-radius: 5px;
}

.multiselect__tag-icon:after {
    content: "\D7";
    color: #333;
    font-size: 14px;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
    background: #ccc;
}

.multiselect__tag-icon:focus:after,
.multiselect__tag-icon:hover:after {
    color: #333;
}

.multiselect__current {
    min-height: 40px;
    overflow: hidden;
    padding: 8px 30px 0 12px;
    white-space: nowrap;
    border-radius: 5px;
    border: 1px solid #e8e8e8;
}

.multiselect__current,
.multiselect__select {
    line-height: 16px;
    box-sizing: border-box;
    display: block;
    margin: 0;
    text-decoration: none;
    cursor: pointer;
}

.multiselect__select {
    position: absolute;
    width: 30px;
    height: 32px;
    right: 1px;
    top: 1px;
    padding: 4px 8px;
    text-align: center;
    transition: transform 0.2s ease;
}

.multiselect__select:before {
    position: relative;
    right: 0;
    top: 65%;
    color: #999;
    margin-top: 4px;
    border-color: #999 transparent transparent;
    border-style: solid;
    border-width: 5px 5px 0;
    content: "";
}

.multiselect__placeholder {
    color: #bfbfbf;
    display: inline-block;
    padding-left: 5px;
    margin: 3px 0px 3px 0px;
    white-space: nowrap;
    /*max-height: 20px;*/
    /*overflow: hidden;*/
    /*position: absolute;*/
}

.multiselect__content-wrapper {
    position: absolute;
    display: block;
    background: #fff;
    width: 100%;
    max-height: 240px;
    overflow: auto;
    border: 1px solid #e8e8e8;
    border-top: none;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    z-index: 50;
    -webkit-overflow-scrolling: touch;
    line-height: 0;
}

.multiselect__content {
    list-style: none;
    display: inline-block;
    padding: 0;
    margin: 0;
    min-width: 100%;
    vertical-align: top;
}

.multiselect--above .multiselect__content-wrapper {
    bottom: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    border-bottom: none;
    border-top: 1px solid #e8e8e8;
}

.multiselect__content::webkit-scrollbar {
    display: none;
}

.multiselect__element {
    display: block;
}

.multiselect__option {
    display: block;
    padding: 4px 4px 4px 6px;
    min-height: 20px;
    line-height: 16px;
    text-decoration: none;
    text-transform: none;
    vertical-align: middle;
    position: relative;
    cursor: pointer;
    white-space: nowrap;
}

.multiselect__option:after {
    top: 0;
    right: 0;
    position: absolute;
    line-height: 20px;
    padding-right: 12px;
    padding-left: 0px;
    font-size: 13px;
}

.multiselect__option--highlight {
    background: #5897fb;
    outline: none;
    color: #fff;
}

.multiselect__option--highlight:after {
    content: attr(data-select);
    background: #5897fb;;
    color: #fff;
}

.multiselect__option--selected {
    background: #f8f8f8;
    color: #333333;
    cursor: no-drop;
    font-weight: 700;
}

.multiselect__option--selected:after {
    content: attr(data-selected);
    color: silver;
}

.multiselect__option--selected.multiselect__option--highlight {
    background: #f8f8f8;
    color: #333333;
}

.multiselect__option--selected.multiselect__option--highlight:after {
    background: #f8f8f8;
    color: silver;
    content: attr(data-deselect);
}

.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__select {
    /*background: #ededed;*/
    color: #a6a6a6;
    cursor: not-allowed;
}

.multiselect--disabled .multiselect__tag-icon {
    cursor: not-allowed;
}

.multiselect__option--disabled {
    /*background: #ededed !important;*/
    /*color: #a6a6a6 !important;*/
    color: silver !important;
    cursor: text;
    pointer-events: none;
    font-weight: normal;
}

.multiselect__option--group {
    /*background: #ededed;*/
    color: #35495e;
    font-weight: bold !important;
    padding: 4px 4px 4px 12px;
}

.multiselect__option--group.multiselect__option--highlight {
    background: #35495e;
    color: #fff;
}

.multiselect__option--group.multiselect__option--highlight:after {
    background: #35495e;
}

.multiselect__option--disabled.multiselect__option--highlight {
    background: #dedede;
}

.multiselect__option--group-selected.multiselect__option--highlight {
    background: #35495e;
    color: #fff;
    cursor: no-drop;
}

.multiselect__option--group-selected.multiselect__option--highlight:after {
    background: #35495e;
    content: attr(data-deselect);
    color: #fff;
}

.multiselect-enter-active,
.multiselect-leave-active {
    transition: all 0.15s ease;
}

.multiselect-enter,
.multiselect-leave-active {
    opacity: 0;
}

.multiselect__strong {
    margin-bottom: 8px;
    line-height: 20px;
    display: inline-block;
    vertical-align: top;
}

[dir="rtl"] .multiselect {
    text-align: right;
}

[dir="rtl"] .multiselect__select {
    right: auto;
    left: 1px;
}

[dir="rtl"] .multiselect__tags {
    padding: 8px 8px 0 40px;
}

[dir="rtl"] .multiselect__content {
    text-align: right;
}

[dir="rtl"] .multiselect__option:after {
    right: auto;
    left: 0;
}

[dir="rtl"] .multiselect__clear {
    right: auto;
    left: 12px;
}

[dir="rtl"] .multiselect__spinner {
    right: auto;
    left: 1px;
}

@keyframes spinning {
    0% {
        transform: rotate(0);
    }
    to {
        transform: rotate(2turn);
    }
}

.full-width {
    display: block;
    width: 100%;
}

.item-bold {
    font-weight: bold;
}

.error {
    &.form-group {
        padding-bottom: 0px;
    }
    i, .app-icon, input {
        color: red;
    }
    .span-red {
        color: red;
    }
    .multiselect__tags {
        border: 1px solid red !important;
    }
}
.caret-wrapper {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
}
</style>
